import { useState, type ReactNode } from 'react';
import type { QuotaExceededDetailsDto } from '@shared/api-dto';
import { useSession } from '../auth/AuthProvider';
import { type Outcome } from '../api/errors';

/**
 * A manual retry, offered on every denial screen.
 *
 * The server resolves entitlement fresh on every request (no cache), so once an admin grants a seat
 * or a plan activates, the change is effective on the very next call — this button just re-fetches
 * so a browser sitting on a stale denial can pick it up. We deliberately don't auto-poll, and never
 * cache a denial client-side.
 */
export function RetryNote({ onRetry }: { onRetry: () => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <p>
      <button
        type="button"
        className="btn-ghost"
        data-testid="retry"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          Promise.resolve(onRetry()).finally(() => setBusy(false));
        }}
      >
        {busy ? 'בודק…' : 'בדקו שוב'}
      </button>{' '}
      <span>אם ההרשאות שלכם עודכנו זה עתה, בדקו שוב.</span>
    </p>
  );
}

export function NeedsPlan({ onShowPlans, onRetry }: { onShowPlans: () => void; onRetry: () => void }) {
  const { can, me } = useSession();
  // Only owners hold billing:checkout:create. Showing a member the pricing page would walk them into
  // a 403 at the checkout button — the plans page is not theirs to act on, so don't offer it. Same
  // reasoning as NeedsSeat: name the person who CAN unblock them instead of a dead end.
  if (!can('billing:checkout:create')) {
    return (
      <div className="huaca-state" data-testid="needs-plan-ask-owner">
        <h2>לארגון שלכם עדיין אין את המוצר הזה</h2>
        <p>בקשו מבעלים של {me?.org.name} לבחור תוכנית. תקבלו גישה מיד לאחר מכן.</p>
        <RetryNote onRetry={onRetry} />
      </div>
    );
  }
  return (
    <div className="huaca-state" data-testid="needs-plan">
      <h2>לארגון שלכם עדיין אין את המוצר הזה</h2>
      <button type="button" className="btn-primary" onClick={onShowPlans}>
        צפייה בתוכניות
      </button>
    </div>
  );
}

export function NeedsSeat({ onRetry }: { onRetry: () => void }) {
  const { me } = useSession();
  return (
    <div className="huaca-state" data-testid="needs-seat">
      <h2>עדיין אין לכם מושב</h2>
      {/* NOT a plans page: the org IS paying — a seat just hasn't been assigned to this person.
          Sending them to checkout would sell a second subscription they don't need. */}
      <p>לארגון שלכם יש את המוצר, אך לא הוקצה לכם מושב. בקשו ממנהל של {me?.org.name} להוסיף אתכם.</p>
      <RetryNote onRetry={onRetry} />
    </div>
  );
}

export function NotPermitted() {
  return (
    <div className="huaca-state" data-testid="not-permitted">
      <h2>אין לכם גישה לפעולה הזו</h2>
      <p>התפקיד שלכם בארגון אינו כולל אותה. מנהל יכול לשנות את התפקידים שלכם.</p>
    </div>
  );
}

export function QuotaExceeded({ quota, onRetry }: { quota: QuotaExceededDetailsDto | null; onRetry: () => void }) {
  const when = quota ? new Date(quota.resetAt) : null;
  const window = quota?.window === 'min' ? 'דקה' : quota?.window === 'mon' ? 'חודש' : 'יום';
  return (
    <div className="huaca-state" data-testid="quota-exceeded">
      <h2>ניצלתם את מכסת ה{window}</h2>
      {quota && (
        <p>
          התוכנית שלכם מאפשרת {quota.limit} ל{window}. מתאפסת ב־
          {when ? when.toLocaleString('he-IL', { dateStyle: 'medium', timeStyle: 'short' }) : 'בקרוב'}.
        </p>
      )}
      <RetryNote onRetry={onRetry} />
    </div>
  );
}

/** Render the right screen for an outcome — the single place the mapping lives. */
export function OutcomeView({
  outcome,
  onShowPlans,
  onRetry,
  children,
}: {
  outcome: Outcome | null;
  onShowPlans: () => void;
  onRetry: () => void;
  children?: ReactNode;
}) {
  if (!outcome) return <>{children}</>;
  switch (outcome.kind) {
    case 'needs-plan':
      return <NeedsPlan onShowPlans={onShowPlans} onRetry={onRetry} />;
    case 'needs-seat':
      return <NeedsSeat onRetry={onRetry} />;
    case 'not-permitted':
      return <NotPermitted />;
    case 'quota-exceeded':
      return <QuotaExceeded quota={outcome.quota} onRetry={onRetry} />;
    case 'rate-limited':
      return <p data-testid="rate-limited">יותר מדי בקשות — האטו ונסו שוב בעוד רגע.</p>;
    case 'not-configured':
      // Nothing the user does can fix this — don't offer a retry that will always fail.
      return (
        <p role="alert" data-testid="not-configured">
          התכונה עדיין לא זמינה. שום דבר לא תקול מצדכם — נסו שוב מאוחר יותר.
        </p>
      );
    case 'trial-used':
      return (
        <p role="alert" data-testid="trial-used">
          כבר השתמשתם בהתנסות החינם של המוצר. בחרו תוכנית בתשלום כדי להמשיך.
        </p>
      );
    case 'conflict':
      return <p role="alert">{outcome.message}</p>;
    case 'unauthenticated':
      return <p data-testid="unauthenticated">פג תוקף החיבור. התחברו שוב.</p>;
    default:
      return <p role="alert">משהו השתבש.</p>;
  }
}
